<template>
  <div class="smart-recruiters-postings">
    <grid-container-fi>
      <div class="smart-recruiters-postings__container">
        <div class="smart-recruiters-postings__headings">
          <smart-recruiters-postings-heading
            :text="$dict.get('smart-Recruiters.Result-Heading-Title')"
            :sort-direction="sortDirection"
            :sort-type="sortType"
            column="name"
            class="smart-recruiters-postings__heading--name"
            @click="setSort"
          />
          <smart-recruiters-postings-heading
            :text="$dict.get('smart-Recruiters.Result-Heading-Location')"
            :sort-direction="sortDirection"
            :sort-type="sortType"
            column="jobLocation"
            class="smart-recruiters-postings__heading"
            @click="setSort"
          />
          <smart-recruiters-postings-heading
            :text="$dict.get('smart-Recruiters.Result-Heading-Entry-Type')"
            :sort-direction="sortDirection"
            :sort-type="sortType"
            column="entryType"
            class="smart-recruiters-postings__heading"
            @click="setSort"
          />
          <smart-recruiters-postings-heading
            :text="$dict.get('smart-Recruiters.Result-Heading-Field-Of-Activity')"
            :sort-direction="sortDirection"
            :sort-type="sortType"
            column="fieldOfActivity"
            class="smart-recruiters-postings__heading"
            @click="setSort"
          />
          <smart-recruiters-postings-heading
            :text="$dict.get('smart-Recruiters.Result-Heading-Department')"
            :sort-direction="sortDirection"
            :sort-type="sortType"
            column="department"
            class="smart-recruiters-postings__heading--dep"
            @click="setSort"
          />
        </div>

        <a
          v-for="post in sortedPostings.slice(0, shownRows)"
          :key="post.id"
          class="smart-recruiters-postings__link"
          :href="getPostLink(post)"
          :target="getLink('detailPageLink').target"
        >
          <grid-columns-fi
            class="smart-recruiters-postings__content"
            :columns="12"
          >
            <div class="smart-recruiters-postings__col--name">
              <div class="smart-recruiters-postings__name-text">
                {{ post.name }}
              </div>
              <div class="smart-recruiters-postings__arrow-wrapper">
                <arrow-icon class="smart-recruiters-postings__arrow" />
              </div>
            </div>
            <div class="smart-recruiters-postings__col--loc">
              <span class="smart-recruiters-postings__mobile-heading">
                {{ $dict.get('smart-Recruiters.Result-Heading-Location') }}
              </span>
              {{ post.jobLocation }}
            </div>
            <div class="smart-recruiters-postings__col">
              <span class="smart-recruiters-postings__mobile-heading">
                {{ $dict.get('smart-Recruiters.Result-Heading-Entry-Type') }}
              </span>
              {{ post.entryType }}
            </div>
            <div class="smart-recruiters-postings__col">
              <span class="smart-recruiters-postings__mobile-heading">
                {{ $dict.get('smart-Recruiters.Result-Heading-Field-Of-Activity') }}
              </span>
              {{ post.fieldOfActivity }}
            </div>
            <div
              class="smart-recruiters-postings__col--dep"
              :department-nr="post.departmentNr"
            >
              <span class="smart-recruiters-postings__mobile-heading">
                {{ $dict.get('smart-Recruiters.Result-Heading-Department') }}
              </span>
              {{ post.department }}
            </div>
          </grid-columns-fi>
        </a>
        <div class="fi-col-span-12">
          <pagination-progress-bar
            :pagination-page="currentPage"
            :pagination-total="totalPages"
            :progressbar-value="percentagePagination"
            class="smart-recruiters-postings__pagination"
            @loadmore="currentPage += 1"
          >
            <span>{{
              $dict.get('smart-Recruiters.Pagination', [shownRows, postings.length])
            }}</span>
          </pagination-progress-bar>
        </div>
      </div>
    </grid-container-fi>
  </div>
</template>

<script setup lang="ts">
import { ArrowIcon, PaginationProgressBar } from 'atoms';
import { computed, ref } from 'vue';

import GridColumnsFi from '@/components/grid-fi/GridColumnsFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useFischer } from '@/composables/fischerPlugin';
import { getLinkData } from '@/utils/linkHelper';

import { departmentId, entryTypeId, fieldOfActivityId } from '../CustomFields';
import SmartRecruitersPostingsHeading from './SmartRecruitersPostingsHeading.vue';

const pageSize = 10;

defineOptions({ name: 'SmartRecruitersPostings' });

const props = withDefaults(
  defineProps<{
    fields?: Record<string, any>;
    postings: any[];
  }>(),
  {
    fields: () => ({}),
  },
);

const fischer = useFischer();

const sortType = ref('');
const sortDirection = ref(true);
const currentPage = ref(1);

function getCustomFieldLabel(post: any, id: string) {
  if (post.customField) {
    const field = post.customField.find((customField: any) => customField.fieldId === id);
    if (field) {
      return field.valueLabel;
    }
  }
  return '';
}

function getCustomFieldValue(post: any, id: string) {
  if (post.customField) {
    const field = post.customField.find((customField: any) => customField.fieldId === id);
    if (field) {
      return field.valueId;
    }
  }
  return '';
}

function getLink(name: string) {
  return getLinkData(props.fields, name);
}

function getPostLink(post: any) {
  const detailPageUrl = getLink('detailPageLink').url;
  const nameKebab = post.name
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLocaleLowerCase();
  const url = `${detailPageUrl}/${nameKebab}-${Number(post.id)}`;

  if (IS_SHOWROOM) {
    // unfortunately the mock router does not accept wildcard routes
    return `${detailPageUrl}?urlMock=${url}`;
  }
  return url;
}

function setSort(key: any) {
  if (key === sortType.value) {
    sortDirection.value = !sortDirection.value;
  } else {
    sortType.value = key;
    sortDirection.value = true;
  }
}

const shownRows = computed(() => Math.min(currentPage.value * pageSize, props.postings.length));
const percentagePagination = computed(() => 100 * (shownRows.value / props.postings.length));
const totalPages = computed(() => Math.ceil(props.postings.length / pageSize));

const formattedPostings = computed(() =>
  props.postings.map((post: any) => ({
    id: post.id,
    name: post.name,
    jobLocation: post.location ? post.location.city : '',
    entryType: getCustomFieldLabel(post, entryTypeId),
    fieldOfActivity: getCustomFieldLabel(post, fieldOfActivityId),
    department: getCustomFieldLabel(post, departmentId),
    departmentNr: getCustomFieldValue(post, departmentId),
  })),
);

const sortedPostings = computed(() => {
  const key = sortType.value || 'name';
  const [lower, higher] = sortDirection.value ? [-1, 1] : [1, -1];
  return formattedPostings.value.slice().sort((a: any, b: any) => {
    if (a[key] === b[key]) {
      // first tie breaker: name
      if (a.name === b.name) {
        // second tie breaker: id
        if (a.id === b.id) {
          console.error('doublicate id', a, b);
          return 0;
        }
        return a.id < b.id ? lower : higher;
      }
      return b.name === '' || (a.name !== '' && a.name < b.name) ? lower : higher;
    }
    return b[key] === '' || (a[key] !== '' && a[key] < b[key]) ? lower : higher;
  });
});

// created
const page = fischer.query('page');
if (page) {
  currentPage.value = Number(page);
}
</script>

<style scoped src="./smart-recruiters-postings" />
